import { useId } from 'react';
import { Input, type InputProps } from '@src/components/Input/Input';
import { Stack } from '@src/components/Stack/Stack';
import { EmailDeliveryWarning } from '@src/components/EmailDeliveryWarning/EmailDeliveryWarning';
import { emailDeliveryIssueDomain } from '@src/components/EmailDeliveryWarning/emailDeliveryIssues';

export type EmailInputProps = Omit<InputProps, 'type' | 'password' | 'numeric'>;

/** Email-specific advice lives here; the generic Input stays domain-agnostic. */
export function EmailInput({
  id,
  value,
  'aria-describedby': describedBy,
  ...props
}: EmailInputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const warningId = `${inputId}-delivery-warning`;
  const domain = emailDeliveryIssueDomain(value);
  // Preserve Input's existing validation and hint descriptions as well.
  const descriptions =
    [
      describedBy,
      props.error ? `${inputId}-error` : undefined,
      props.hint ? `${inputId}-hint` : undefined,
      domain ? warningId : undefined,
    ]
      .filter(Boolean)
      .join(' ') || undefined;
  return (
    <Stack gap={2} wide>
      <Input
        autoComplete="email"
        autoCapitalize="none"
        spellCheck={false}
        {...props}
        id={inputId}
        type="email"
        value={value}
        aria-describedby={descriptions}
      />
      <EmailDeliveryWarning email={value} id={warningId} />
    </Stack>
  );
}
